import React, { useEffect, useState } from "react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Building2, CheckCircle2, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira } from "@/lib/format";

// Debtor's "Pay debt" flow — Debii holds no money; transfer happens at the bank.
export default function PayDebtSheet({ open, onOpenChange, debt, onDone }) {
  const [step, setStep] = useState(1); // 1 amount, 2 details, 3 confirm, 4 done
  const [details, setDetails] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    if (open) {
      setStep(1); setAmount(""); setShareUrl("");
      api.get(`/debts/${debt.id}/repayment-details`)
        .then((r) => setDetails(r.data))
        .catch(() => { toast.error("No repayment details available."); onOpenChange(false); });
    }
  }, [open]);

  useEffect(() => {
    if (open) { try { document.body.style.pointerEvents = ""; } catch {} }
    return () => { try { document.body.style.pointerEvents = ""; } catch {} };
  }, [open]);

  if (!details) return null;
  const cur = details.currency || "NGN";
  const outstanding = details.outstanding_amount;
  const amt = parseFloat(amount || "0");

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const goDetails = () => {
    if (!(amt > 0)) return toast.error("Enter an amount.");
    if (amt > outstanding) return toast.error("Amount can't exceed the outstanding balance.");
    setStep(2);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/debts/${debt.id}/report-repayment`, { amount: amt });
      setShareUrl(`${window.location.origin}${data.confirm_path}`);
      setStep(4);
      onDone && onDone();
    } catch (e) {
      toast.error(apiError(e));
      setStep(1);
    } finally { setBusy(false); }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92dvh]">
        <div data-testid="pay-debt-sheet" className="px-5 pb-8 pt-2 overflow-y-auto overscroll-contain">
          {step === 1 && (
            <>
              <h3 className="font-display text-xl font-extrabold text-foreground">Pay {details.person_name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">Outstanding balance</p>
              <p className="font-display text-3xl font-extrabold tabular text-foreground">{fmtNaira(outstanding, cur)}</p>
              <p className="mt-5 text-sm font-semibold text-foreground">How much are you paying?</p>
              <div className="mt-2 flex gap-2">
                <Button variant="outline" className="h-10 rounded-xl flex-1" data-testid="pay-full-btn"
                  onClick={() => setAmount(String(outstanding))}>Pay full balance</Button>
              </div>
              <Input data-testid="pay-amount-input" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                placeholder="Enter another amount" className="mt-3 h-12 rounded-xl tabular text-lg" />
              <Button data-testid="pay-continue-btn" onClick={goDetails} className="mt-5 w-full h-12 rounded-xl">Continue</Button>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="font-display text-xl font-extrabold text-foreground">Pay {fmtNaira(amt, cur)} to:</h3>
              <div className="mt-4 rounded-2xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 text-primary text-sm font-bold mb-2"><Building2 className="h-4 w-4" /> Repayment details</div>
                <p className="font-display text-lg font-extrabold text-foreground">{details.account_name}</p>
                <p className="text-sm text-muted-foreground">{details.bank_name}</p>
                <p className="mt-1 font-display text-xl font-extrabold tabular text-foreground" data-testid="pay-acct-number">{details.account_number}</p>
                <Button variant="outline" className="mt-3 w-full h-11 rounded-xl" data-testid="pay-copy-acct"
                  onClick={() => copy(details.account_number)}><Copy className="h-4 w-4" /> Copy account number</Button>
              </div>
              <p className="mt-4 text-xs text-muted-foreground text-center">
                Make the transfer in your banking app, then come back and report it. Debii doesn't move the money.
              </p>
              <Button data-testid="pay-made-payment-btn" onClick={() => setStep(3)} className="mt-4 w-full h-12 rounded-xl">
                I've made the payment
              </Button>
            </>
          )}

          {step === 3 && (
            <>
              <h3 className="font-display text-xl font-extrabold text-foreground">Did you pay {fmtNaira(amt, cur)} to {details.person_name}?</h3>
              <div className="mt-3 rounded-xl bg-secondary p-3 text-sm">
                <span className="text-muted-foreground">Account: </span>
                <span className="font-semibold text-foreground">{details.bank_name} · {details.account_masked}</span>
              </div>
              <Button data-testid="pay-submit-btn" onClick={submit} disabled={busy} className="mt-5 w-full h-12 rounded-xl">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, submit payment"}
              </Button>
              <button className="mt-3 w-full text-center text-sm text-muted-foreground" onClick={() => setStep(2)}>Cancel</button>
            </>
          )}

          {step === 4 && (
            <>
              <div className="flex flex-col items-center text-center py-4">
                <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                <h3 className="mt-3 font-display text-xl font-extrabold text-foreground">Payment reported</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your {fmtNaira(amt, cur)} payment has been sent to {details.person_name} for confirmation.
                  Your balance updates once they confirm.
                </p>
              </div>
              <Button variant="outline" className="w-full h-11 rounded-xl" data-testid="pay-share-confirm"
                onClick={() => copy(shareUrl)}><Share2 className="h-4 w-4" /> Copy confirmation link for {details.person_name}</Button>
              <Button className="mt-3 w-full h-12 rounded-xl" data-testid="pay-done-btn"
                onClick={() => onOpenChange(false)}>Done</Button>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
